import { useQuery } from "@tanstack/react-query";
import { BrandQuickLinks } from "@/components/home/BrandQuickLinks";
import { BrandsStrip } from "@/components/home/BrandsStrip";
import { CategoryQuickLinks } from "@/components/home/CategoryQuickLinks";
import { HomeHero } from "@/components/home/HomeHero";
import { ProductGrid } from "@/components/home/ProductGrid";
import { PromoWhatsAppModal } from "@/components/home/PromoWhatsAppModal";
import { SeoIntro } from "@/components/home/SeoIntro";
import {
  getCatalogBrands,
  getCatalogProducts,
  getCategoryTree,
  type ProductListItem,
} from "@/lib/api-client";
import { getWhatsAppContact } from "@/lib/contact";

/** Tag produit à poser depuis l'admin pour mettre un produit « en pub » dans le modal d'accueil. */
const PROMO_MODAL_TAG = "promo-accueil";
const PROMO_MODAL_LIMIT = 12;

/**
 * Produits du modal WhatsApp d'accueil : d'abord ceux taggés `promo-accueil` (produits sur
 * lesquels une pub tourne), puis ceux en promotion (prix barré), sans doublon. Seuls les
 * produits en stock sont proposés — inutile d'attirer un prospect sur une rupture.
 */
async function loadPromoProducts(): Promise<ProductListItem[]> {
  const [tagged, onSale] = await Promise.all([
    getCatalogProducts({ tag: PROMO_MODAL_TAG, inStock: true, limit: PROMO_MODAL_LIMIT })
      .then((response) => response.items)
      .catch(() => [] as ProductListItem[]),
    getCatalogProducts({ onSale: true, inStock: true, limit: PROMO_MODAL_LIMIT })
      .then((response) => response.items)
      .catch(() => [] as ProductListItem[]),
  ]);

  const seen = new Set<string>();
  return [...tagged, ...onSale]
    .filter((product) => (seen.has(product.id) ? false : (seen.add(product.id), true)))
    .slice(0, PROMO_MODAL_LIMIT);
}

/**
 * Équivalent de apps/web/app/(shop)/page.tsx. Les trois `Promise.all(...)`
 * défensifs (chaque source retombe sur `[]` en cas d'échec, cf. commentaire
 * d'origine « une section sans donnée ne s'affiche pas ») deviennent trois
 * `useQuery()` indépendants : chaque section (`CategoryQuickLinks`,
 * `ProductGrid`, `BrandsStrip`) sait déjà se masquer elle-même si son
 * tableau est vide, donc `data ?? []` reproduit le même repli, que l'échec
 * vienne d'une réponse vide ou d'une erreur réseau — pas de plantage de toute
 * la page pour une seule section indisponible.
 *
 * Clé `["catalog", "category-tree"]` volontairement identique à celle déjà
 * utilisée par `SiteHeader` (src/components/layout/Header.tsx) : partage le
 * même cache TanStack Query (`staleTime` 30s), pas de second appel réseau.
 */
export function HomePage() {
  const { data: categories } = useQuery({
    queryKey: ["catalog", "category-tree"],
    queryFn: getCategoryTree,
  });

  const { data: newestProducts } = useQuery({
    queryKey: ["catalog", "products", "newest", 10],
    queryFn: () => getCatalogProducts({ sort: "newest", limit: 10 }),
  });

  const { data: brands } = useQuery({
    queryKey: ["catalog", "brands"],
    queryFn: getCatalogBrands,
  });

  const { data: promoProducts } = useQuery({
    queryKey: ["catalog", "products", "promo-modal"],
    queryFn: loadPromoProducts,
  });

  // Même clé que WhatsAppFloatButton/Footer : cache partagé, pas de second appel réseau.
  const { data: whatsapp } = useQuery({
    queryKey: ["settings", "whatsapp-contact"],
    queryFn: getWhatsAppContact,
  });

  return (
    <>
      <PromoWhatsAppModal products={promoProducts ?? []} whatsAppLink={whatsapp?.link ?? "#"} />
      <HomeHero />
      <ProductGrid title="Nouveautés" viewAllHref="/catalogue?sort=newest" products={newestProducts?.items ?? []} />
      <BrandQuickLinks brands={brands ?? []} />
      <CategoryQuickLinks categories={categories ?? []} />
      <BrandsStrip brands={brands ?? []} />
      <SeoIntro />
    </>
  );
}
